import React from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import moment from 'moment';
import { compose, withHandlers, withProps, branch } from 'recompose';
import classNames from 'classnames';

import styles from './timeplan.css';
import TimeplanDay from './TimeplanDay';
import TimeplanDayCopy from './TimeplanDayCopy';
import { Checkbox } from '..';
import MileageAllowance from './MileageAllowance';
import { useTranslation } from 'react-i18next';

const getStatusColor = (tpu) => {
  if (!tpu || !tpu.internalStatus) {
    return undefined;
  }

  if (tpu.internalStatus === 'ATTESTED') {
    return '#dff0d8';
  }

  if (tpu.internalStatus === 'REPORTED') {
    return '#ffe0b2';
  }

  return '#f9bdbb';
};

function SelectedDate({
  date,
  disabled,
  renderFooter,
  onChangeTimeplanUnits,
  selectedDates,
  timeplanUnits,
  isEditable,
  useIndicate,
  useIndicateBox,
  switchIndicate,
  expensesDates,
  approvedDates,
  dayComments,
  mileageAllowances,
  allMileageAllowances,
  isHolidayDate,
  ...props
}) {
  const { t } = useTranslation();
  const firstTpu = timeplanUnits.first() || mileageAllowances.first();
  const dayComment = dayComments.get(date);
  return (
    <div
      key={date}
      className={classNames('col-md-1', styles.sevencols, {
        [styles.selected]: firstTpu && !!firstTpu.indicateHandle,
      })}
      style={{
        background: getStatusColor(firstTpu),
      }}
    >
      <div
        className={
          classNames({
            [styles.nonEditableSelectedDate]: !isEditable,
            [styles.indicateBox]: useIndicateBox,
          })
        }
        onClick={useIndicate && !isEditable ? switchIndicate : undefined}
      >
        {useIndicate && !!firstTpu && (
          <span
            className={classNames(styles.timeplanDate, {
              'text-danger': isHolidayDate,
            })}
            role="button"
            tabIndex="0"
            onClick={switchIndicate}
          >
            {moment(date).format('ddd D/M')}
            {expensesDates.contains(date) && (
              <i
                className="fa fa-money pull-right"
                title={t("Det ligger kostnader att hantera på denna dagen")}
              />
            )}
            {approvedDates.has(date) && (
              <i
                className={classNames(
                  'fa pull-right fa-building',
                  {
                    'text-success': approvedDates.get(date).approved === true,
                    'text-danger': approvedDates.get(date).approved === false,
                    'text-warning': approvedDates.get(date).approved === undefined,
                  },
                )}
                title={approvedDates.get(date).comment}
              />
            )}
          </span>
        )}

        {(!useIndicate || !firstTpu) && (
          <span
            className={classNames(styles.timeplanDate, {
              'text-danger': isHolidayDate,
            })}
          >
            {moment(date).format('ddd D/M')}
          </span>
        )}

        {firstTpu && useIndicate && (
          <span className={classNames(
            styles.checkbox,
            { clearfix: !isEditable })
          }
          >
            <Checkbox
              input={{
                name: 'indicateHandle',
                onBlur: () => { },
                onDragStart: () => { },
                onDrop: () => { },
                onFocus: () => { },
                onChange: switchIndicate,
                value: !!firstTpu.indicateHandle,
              }}
              noIndicator
            />
          </span>
        )}

        {isEditable && (
          <TimeplanDayCopy
            disabled={disabled}
            date={date}
            selectedDates={selectedDates}
            timeplanUnits={timeplanUnits}
            onChangeTimeplanUnits={onChangeTimeplanUnits}
          />
        )}

        <TimeplanDay
          isEditable={isEditable}
          disabled={disabled}
          date={date}
          timeplanUnits={timeplanUnits}
          onChangeTimeplanUnits={onChangeTimeplanUnits}
          hasMileageAllowanceOnDate={mileageAllowances.size > 0}
          hasMileageAllowance={allMileageAllowances.size > 0}
          {...props}
        />

        {dayComment && (
          <div
            className={styles.dayComment}
            title={dayComment}
          >
            <i className="fa fa-comment-o" />
            <span>{dayComment}</span>
          </div>
        )}

        {
          // Hack to get mileageAllowance into the same structure,
          // since this isn't based originally on redux-forms
        }
        <MileageAllowance
          mileageAllowances={mileageAllowances}
          isEditable={isEditable}
          disabled={disabled}
          onChangeTimeplanUnits={onChangeTimeplanUnits}
        />

        {renderFooter ? renderFooter(date) : null}
      </div>
    </div>
  );
}

SelectedDate.defaultProps = {
  renderFooter: undefined,
  selectedDates: new List(),
  useIndicate: false,
  useIndicateBox: false,
  expensesDates: new List(),
  approvedDates: new Map(),
  dayComments: new Map(),
  switchIndicate: undefined,
  mileageAllowances: new List(),
  allMileageAllowances: new List(),
  isHolidayDate: false,
};

SelectedDate.propTypes = {
  date: PropTypes.string.isRequired,
  isEditable: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  renderFooter: PropTypes.func,
  timeplanUnits: PropTypes.instanceOf(List).isRequired,
  selectedDates: PropTypes.instanceOf(List),
  onChangeTimeplanUnits: PropTypes.func.isRequired,
  switchIndicate: PropTypes.func,
  useIndicate: PropTypes.bool,
  useIndicateBox: PropTypes.bool,
  expensesDates: PropTypes.instanceOf(List),
  approvedDates: PropTypes.instanceOf(Map),
  dayComments: PropTypes.instanceOf(Map),
  mileageAllowances: PropTypes.instanceOf(List),
  allMileageAllowances: PropTypes.instanceOf(List),
  isHolidayDate: PropTypes.bool,
};

export default compose(
  withHandlers({
    switchIndicate: ({
      allMileageAllowances,
      allTimeplanUnits,
      onChangeTimeplanUnits,
      date,
    }) => () => {
      const tpusAndMileageAllowances = allTimeplanUnits.concat(allMileageAllowances);
      const hasIndicateHandle = !!tpusAndMileageAllowances
        .filter(period => period.date === date)
        .find(({ indicateHandle }) => !!indicateHandle);
      const newTimeplanUnits = tpusAndMileageAllowances
        .map((tpu) => {
          if (date === tpu.date) {
            return tpu.merge({ indicateHandle: !hasIndicateHandle });
          }
          return tpu;
        });
      const transformFunc = (o, tpus) => tpus;
      onChangeTimeplanUnits(transformFunc, newTimeplanUnits);
    },
  }),
  branch(({ holidayDates }) => !!holidayDates,
    withProps(({ holidayDates, date }) => ({
      isHolidayDate: holidayDates.includes(date)
    })),
  ),
)(SelectedDate);
